import { FragmentType, getFragmentData } from '@/__generated__';
import { OrderLineStatus, OrderLineType, OrderStatus } from '@/__generated__/graphql';
import { useMutation } from '@apollo/client';
import { ActionIcon, Badge, Button, Checkbox, Group, Radio, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconEdit, IconExclamationMark, IconPlus, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { orderLineDetailsFragment } from '../graphql/fragments';
import {
  removeLineFromOrderDocument,
  sendMessageDocument,
  updateLineOnOrderDocument,
  updateOrderDocument,
  upsertLineOnOrderDocument,
} from '../graphql/mutations';
import { OrderLineForm, OrderLineInput } from './OrderLineForm';

const lineStatusColors = {
  [OrderLineStatus.Created]: 'gray',
  [OrderLineStatus.Packed]: 'blue',
  [OrderLineStatus.Confirmed]: 'green',
  [OrderLineStatus.Reviewing]: 'orange',
  [OrderLineStatus.Returned]: 'red',
};

const lineStatusTranslations = {
  [OrderLineStatus.Created]: 'Objednané',
  [OrderLineStatus.Packed]: 'Zabalené',
  [OrderLineStatus.Confirmed]: 'Potvrdené',
  [OrderLineStatus.Reviewing]: 'V riešení',
  [OrderLineStatus.Returned]: 'Vrátené',
};

// Stored as-is in OrderLineCancellation.reason — reasons in this app are
// display-ready Slovak text (same as the customer block reason), no mapping.
const cancellationReasons = ['Výmena', 'Zrušenie', 'Problém'];

const CancelLineModal = ({
  orderId,
  lineId,
  lineCode,
  lineType,
}: {
  orderId: string;
  lineId: string;
  lineCode: string;
  lineType: OrderLineType;
}) => {
  const [reason, setReason] = useState('');
  // The trash button sits on every line, not just products, and the modal title
  // says "produkt" regardless — so only pre-tick for real product lines, or a
  // removed shipping line would message "Produkt Doprava bol zrušený".
  const [notify, setNotify] = useState(lineType === OrderLineType.Product);

  const [removeLineFromOrder, { loading }] = useMutation(removeLineFromOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [sendMessage, sendMessageMutation] = useMutation(sendMessageDocument);

  return (
    <Stack>
      <Radio.Group value={reason} onChange={setReason} label="Dôvod zrušenia" withAsterisk>
        <Stack gap="xs" mt="xs">
          {cancellationReasons.map((value) => (
            <Radio key={value} value={value} label={value} />
          ))}
        </Stack>
      </Radio.Group>
      <Checkbox
        checked={notify}
        onChange={(event) => setNotify(event.currentTarget.checked)}
        label="Informovať zákazníka"
      />
      <Button
        color="red"
        disabled={!reason}
        loading={loading || sendMessageMutation.loading}
        onClick={async () => {
          const { data } = await removeLineFromOrder({ variables: { lineId, reason } });

          // Sent after the cancel so the total is the post-removal one, and so a
          // failed cancel never messages the customer. The reverse failure is
          // survivable — the line is already gone — so it only notifies.
          const total = data?.removeLineFromOrder.total;

          // Never interpolate a missing total into customer-facing text — a
          // "suma: undefined €" message is worse than no message at all.
          if (notify && typeof total !== 'number') {
            showNotification({
              title: 'Produkt zrušený, správa neodoslaná',
              message: 'Nepodarilo sa zistiť novú sumu objednávky',
              color: 'red',
            });
          } else if (notify) {
            try {
              await sendMessage({
                variables: {
                  orderId,
                  message:
                    `Produkt ${lineCode} bol zrušený.\n` +
                    `Nová celková suma objednávky: ${total} €`,
                },
              });
            } catch (error) {
              showNotification({
                title: 'Produkt zrušený, správa neodoslaná',
                message: error instanceof Error ? error.message : 'Neznáma chyba',
                color: 'red',
              });
            }
          }

          modals.closeAll();
        }}
      >
        Zrušiť produkt
      </Button>
    </Stack>
  );
};

export const OrderLinesTable = ({
  orderId,
  ...props
}: {
  orderId: string;
  lines?: FragmentType<typeof orderLineDetailsFragment>[] | null;
}) => {
  const lines = getFragmentData(orderLineDetailsFragment, props.lines);

  const [updateLineOnOrder, updateLineOnOrderMutation] = useMutation(updateLineOnOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [upsertLineOnOrder, upsertLineOnOrderMutation] = useMutation(upsertLineOnOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const [updateOrder, updateOrderMutation] = useMutation(updateOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const handleUpsert = async (input: OrderLineInput) => {
    await upsertLineOnOrder({
      variables: {
        orderId,
        input,
      },
    });

    modals.closeAll();
  };

  return (
    <DataTable
      withTableBorder
      striped
      highlightOnHover
      textSelectionDisabled
      borderRadius="sm"
      records={lines ?? []}
      height="auto"
      minHeight={200}
      columns={[
        {
          accessor: 'code',
          title: 'Kód',
          render: ({ code, type }) => (type === OrderLineType.Product ? code : ''),
        },
        { accessor: 'name', title: 'Názov' },
        {
          accessor: 'description',
          title: 'Popis',
        },
        {
          accessor: 'price',
          title: 'Cena',
          render: ({ price }) => `${price} €`,
        },
        {
          accessor: 'status',
          title: 'Stav',
          render: ({ status }) => (
            <Badge color={lineStatusColors[status]}>{lineStatusTranslations[status]}</Badge>
          ),
        },
        {
          accessor: 'actions',
          textAlign: 'right',
          title: (
            <ActionIcon
              size="sm"
              variant="subtle"
              onClick={() =>
                modals.open({
                  children: (
                    <OrderLineForm
                      onSubmit={(input) => {
                        void handleUpsert(input);
                      }}
                    />
                  ),
                })
              }
              loading={upsertLineOnOrderMutation.loading}
            >
              <IconPlus size={16} />
            </ActionIcon>
          ),
          render: (line) => (
            <Group gap={4} justify="right" wrap="nowrap">
              {line.status === OrderLineStatus.Created && (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="orange"
                  onClick={async () => {
                    await updateLineOnOrder({
                      variables: {
                        lineId: line.id,
                        input: { status: { set: OrderLineStatus.Reviewing } },
                      },
                    });

                    await updateOrder({
                      variables: {
                        where: { id: orderId },
                        data: {
                          status: { set: OrderStatus.Reviewing },
                        },
                      },
                    });
                  }}
                  loading={updateLineOnOrderMutation.loading || updateOrderMutation.loading}
                >
                  <IconExclamationMark size={16} />
                </ActionIcon>
              )}
              {line.status === OrderLineStatus.Reviewing && (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={async () => {
                    await updateLineOnOrder({
                      variables: {
                        lineId: line.id,
                        input: { status: { set: OrderLineStatus.Confirmed } },
                      },
                    });

                    await updateOrder({
                      variables: {
                        where: { id: orderId },
                        data: {
                          status: { set: OrderStatus.Confirmed },
                        },
                      },
                    });
                  }}
                  loading={updateLineOnOrderMutation.loading || updateOrderMutation.loading}
                >
                  <IconCheck size={16} />
                </ActionIcon>
              )}

              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() => {
                  modals.open({
                    children: (
                      <OrderLineForm
                        line={line}
                        onSubmit={(input) => {
                          void handleUpsert(input);
                        }}
                      />
                    ),
                  });
                }}
                loading={upsertLineOnOrderMutation.loading}
              >
                <IconEdit size={16} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                onClick={() => {
                  modals.open({
                    title: `Zrušiť produkt ${line.code}?`,
                    children: (
                      <CancelLineModal
                        orderId={orderId}
                        lineId={line.id}
                        lineCode={line.code}
                        lineType={line.type}
                      />
                    ),
                  });
                }}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ),
        },
      ]}
    />
  );
};
